import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as ReTooltip,
  ResponsiveContainer,
  CartesianGrid,
} from 'recharts';
import { Loader2, Users, Download } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useT } from '@/i18n';
import { useHouseholdPromotionStats } from '@/features/admin/reports.api';

/**
 * Cluster H — Household promotion section for ReportsPage.
 *
 * Renders:
 *   - A bar chart placeholder (per-day series not exposed by the KPI endpoint
 *     yet; shows a single aggregate bar for the window — Cluster J / future
 *     can split into daily buckets if the endpoint adds a trend response).
 *   - A "Top agents by promotion" table (KPI endpoint's topAgentsByPromotion).
 *   - Export CSV button disabled (Cluster J integration deferred).
 *
 * Empty state shown when no promotions in the window.
 */
export function HouseholdPromotionSection(): JSX.Element {
  const t = useT();
  const q = useHouseholdPromotionStats(30);
  const s = q.data;

  // Aggregate row used as a single bar (we have NO per-day series from the
  // endpoint yet). Just shows the window totals for context.
  const chartData = useMemo(() => {
    if (!s) return [];
    return [
      {
        label: '30d',
        opened: s.subScreeningsOpenedInWindow,
        submitted: s.subScreeningsSubmittedInWindow,
        abandoned: s.subScreeningsAbandonedInWindow,
      },
    ];
  }, [s]);

  return (
    <section className="rounded-lg border border-nav-card bg-white p-4 space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-nav-tealDark" aria-hidden />
          <h2 className="font-head text-[15px] font-bold text-nav-navy">
            {t('admin.householdPromotion.reports.section')}
          </h2>
        </div>
        <Button variant="secondary" size="sm" disabled>
          <Download className="h-4 w-4" />
          {t('admin.householdPromotion.reports.exportPending')}
        </Button>
      </div>

      {q.isLoading ? (
        <div className="py-8 text-center">
          <Loader2 className="h-5 w-5 text-nav-teal animate-spin mx-auto" />
        </div>
      ) : !s || s.subScreeningsOpenedInWindow + s.promotedInWindow === 0 ? (
        <p className="text-sm text-nav-slate py-6 text-center">
          {t('admin.householdPromotion.reports.empty')}
        </p>
      ) : (
        <>
          {/* Trend chart (placeholder single-bar for now) */}
          <div>
            <p className="text-xs text-nav-slate mb-2">
              {t('admin.householdPromotion.reports.trendTitle')}
            </p>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 5, right: 10, bottom: 5, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef1f3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                  <ReTooltip />
                  <Bar dataKey="opened" fill="#228a8d" name="Opened" />
                  <Bar dataKey="submitted" fill="#5c8a54" name="Submitted" />
                  <Bar dataKey="abandoned" fill="#b0574c" name="Abandoned" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Top agents table */}
          <div>
            <p className="text-xs text-nav-slate mb-2">
              {t('admin.householdPromotion.reports.byAgentTitle')}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-nav-softer text-nav-slate text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-2 py-1.5">
                      {t('admin.householdPromotion.reports.colEmail')}
                    </th>
                    <th className="text-right px-2 py-1.5">
                      {t('admin.householdPromotion.reports.colPromoted')}
                    </th>
                    <th className="text-right px-2 py-1.5">
                      {t('admin.householdPromotion.reports.colSubSubmitted')}
                    </th>
                    <th className="text-right px-2 py-1.5">
                      {t('admin.householdPromotion.reports.colAbandoned')}
                    </th>
                    <th className="text-right px-2 py-1.5">
                      {t('admin.householdPromotion.reports.colAvgDuration')}
                    </th>
                    <th className="text-right px-2 py-1.5">
                      {t('admin.householdPromotion.reports.colRevenue')}
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-nav-hair">
                  {s.topAgentsByPromotion.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-2 py-3 text-center text-xs text-nav-slate">
                        {t('admin.householdPromotion.reports.empty')}
                      </td>
                    </tr>
                  ) : (
                    s.topAgentsByPromotion.map((a) => (
                      <tr key={a.userId} className="hover:bg-nav-soft">
                        <td className="px-2 py-1.5 truncate">{a.email}</td>
                        <td className="text-right px-2 py-1.5 tabular-nums">{a.promotedCount}</td>
                        <td className="text-right px-2 py-1.5 tabular-nums">{a.subSubmittedCount}</td>
                        <td className="text-right px-2 py-1.5 tabular-nums">{a.subAbandonedCount}</td>
                        <td className="text-right px-2 py-1.5 tabular-nums">
                          {Math.round(a.avgSubDurationSec / 60)}m
                        </td>
                        <td className="text-right px-2 py-1.5 tabular-nums">
                          ${a.estimatedRevenueUsd.toFixed(2)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </section>
  );
}
